import mixpanel from "mixpanel-browser";
import { useCallback, useEffect, useState } from "react";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";

// Module-level mutex: only one tour can run at a time.
// Tours that poll for start will wait until the active tour completes.
let activeTourName = null;

export const isAnyTourActive = () => activeTourName !== null;

/**
 * Hook for managing guided tour state, DOM polling, Joyride callback,
 * and Mixpanel tracking.
 *
 * @param {object} options
 * @param {string} options.name           Mixpanel event name
 * @param {Array}  options.steps          Joyride step definitions
 * @param {boolean} options.isActive      Whether the tour should start
 * @param {() => void} options.onComplete Called when tour finishes/closes
 * @param {boolean} [options.pollForStart=true]
 *   Poll for the first step's DOM target before running.
 *   Set false when the consumer manages its own polling (e.g. FTUE).
 * @param {(index: number, action: string, helpers: {setRun, setStepIndex}) => boolean|void} [options.onStepAfter]
 *   Called after each step advance. Return `true` to prevent the
 *   default `setStepIndex(index + 1)`.
 * @param {string} [options.killDate]
 *   ISO 8601 date string (e.g. "2026-04-15"). If the current date is
 *   on or after this date the tour is permanently disabled.
 * @param {() => boolean} [options.canStart]
 *   Optional guard called during poll-for-start. The tour will only
 *   begin when this returns true (in addition to the target being present).
 */
export const useTour = ({
  name,
  steps,
  isActive,
  onComplete,
  pollForStart = true,
  onStepAfter,
  killDate,
  canStart,
}) => {
  const killed = killDate && new Date() >= new Date(killDate);

  const [run, setRunRaw] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Wrap setRun so that starting a tour claims the mutex and is blocked
  // when another tour is already running.
  const setRun = useCallback(
    (value) => {
      if (value) {
        if (activeTourName && activeTourName !== name) return;
        activeTourName = name;
      }
      setRunRaw(value);
    },
    [name],
  );

  // Simple poll: wait for the first step's target to appear in the DOM
  useEffect(() => {
    if (!isActive || killed || !pollForStart) return;

    const interval = setInterval(() => {
      if (activeTourName) return;
      const el = document.querySelector(steps[0]?.target);
      const isVisible =
        el && (el.offsetWidth > 0 || el.offsetHeight > 0);
      if (isVisible && (!canStart || canStart())) {
        clearInterval(interval);
        activeTourName = name;
        mixpanel.track(name, { eventType: "Started" });
        setStepIndex(0);
        setRunRaw(true);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isActive, killed, pollForStart, steps, name, canStart]);

  // Release the mutex if the tour component unmounts mid-tour
  useEffect(() => {
    return () => {
      if (activeTourName === name) {
        activeTourName = null;
      }
    };
  }, [name]);

  const completeTour = () => {
    onComplete();
    setRunRaw(false);
    setStepIndex(0);
    if (activeTourName === name) {
      activeTourName = null;
    }
  };

  const handleCallback = ({ action, index, status, type }) => {
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) ||
      (action === ACTIONS.CLOSE && status === STATUS.RUNNING)
    ) {
      mixpanel.track(name, {
        eventType: action === ACTIONS.CLOSE ? "Closed" : "Finished",
        stepIndex,
      });
      completeTour();
    } else if (type === EVENTS.STEP_AFTER) {
      mixpanel.track(name, {
        eventType: "Advanced",
        stepIndex,
      });
      if (
        onStepAfter?.(index, action, {
          setRun,
          setStepIndex,
        })
      ) {
        return;
      }
      setStepIndex(index + 1);
    }
  };

  return {
    run,
    setRun,
    stepIndex,
    setStepIndex,
    handleCallback,
    completeTour,
  };
};
